import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';

import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useSettingsContext } from 'src/components/settings';
import { useTenant } from 'src/auth/context/firebase/TenantContext';
import axiosInstance from 'src/utils/axios';
import Main from './main';
import Header from './header';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';



// ----------------------------------------------------------------------

export default function DashboardLayout({ children }) {
  const settings = useSettingsContext();
  const { selectedTenant } = useTenant();

  const lgUp = useResponsive('up', 'lg');
  const nav = useBoolean();

  const isHorizontal = settings.themeLayout === 'horizontal';
  const isMini = settings.themeLayout === 'mini';

  const [navData, setNavData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedTenant) {
      setLoading(false);
      return;
    }

    const fetchNavData = async () => {
      try {
        const response = await axiosInstance.get(`${process.env.REACT_APP_API_BASE_URL}/api/Page/tenant/${selectedTenant}`);
        const filteredNavData = response.data
          .filter(item => item.published)
          .map(item => ({
            title: item.name,
            path: item.navRoute,
            // icon: ICONS[item.id.toLowerCase()] || ICONS.default,
          }));
        setNavData(filteredNavData);
      } catch (error) {
        console.error('Error fetching navigation data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNavData();
  }, [selectedTenant]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderNavMini = <NavMini data={navData} />;
  const renderHorizontal = <NavHorizontal data={navData} />;
  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} data={navData} />;

  if (isHorizontal) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />
        {lgUp ? renderHorizontal : renderNavVertical}
        <Main>{children}</Main>
      </>
    );
  }

  if (isMini) {
    return (
      <>
        <Header onOpenNav={nav.onTrue} />
        <Box
          sx={{
            minHeight: 1,
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
          }}
        >
          {lgUp ? renderNavMini : renderNavVertical}
          <Main>{children}</Main>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header onOpenNav={nav.onTrue} />
      <Box
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        {renderNavVertical}
        <Main>{children}</Main>
      </Box>
    </>
  );
}

DashboardLayout.propTypes = {
  children: PropTypes.node,
};
